import { Injectable, inject } from '@angular/core';
import { TranslateParser, TranslateService } from '@ngx-translate/core';

import { CodedStatusMessage } from '@gfe/core/models/coded-status-message.model';
import { formatDurationFromIso, isIsoDuration } from '@gfe/core/utils/time-utils';

import { LocalizedText } from '@core/models/error-catalog.model';
import { dbKpiId } from '@core/models/kpi.model';
import { EntityDisplayNamesService } from '@core/services/entity-display-names/entity-display-names.service';
import { ErrorCatalogService } from '@core/services/error-catalog/error-catalog.service';
import {
  capitalizeFirst,
  toCamelCase,
  toScreamingSnakeCase,
  toSnakeCase
} from '@core/utils/case-conversions';
import { formatIsoDateTimeAsStated } from '@core/utils/date-utils';
import { formatQuantity } from '@core/utils/quantity-utils';

import { refLabel } from './entity-ref-labels';
import { findingSize } from './finding-size';
import { interpolateStrict, interpolateStrictTemplate } from './interpolate-strict';
import { printedKind } from './printed-kind';
import { relationParams, relationRefs } from './relation-params';
import { normalize } from './warning-normalizer';
import {
  FormattedField,
  MessagePart,
  NormalizedWarning,
  WarningOccurrence,
  WarningView
} from './warning.model';

/** Matches ngx-translate's own `templateMatcher`, scanning globally to collect every name. */
const PLACEHOLDER_NAMES = /{{\s?([^{}\s]*)\s?}}/g;

/** Every `{{name}}` a template addresses, before any value is substituted in. */
function placeholdersIn(template: string): Set<string> {
  const names = new Set<string>();
  for (const match of template.matchAll(PLACEHOLDER_NAMES)) {
    names.add(match[1]);
  }
  return names;
}

/**
 * Stands in for one interpolated value so the rendered template shows where each value
 * landed. Private-use code points never occur in a message and pass through
 * ngx-translate's parser untouched.
 */
const slotMarker = (index: number) => `\uE000${index}\uE001`;
const SLOT_MARKER = /\uE000(\d+)\uE001/g;

/** One interpolated value: where it sits in the params, and the text it rendered as. */
interface Slot {
  path: string[];
  text: string;
}

/** Field names whose value is an opaque identifier, tested against the wire spelling. */
const UID_FIELD = /(^|_)uids?$/;

const backendKpiIdMap = Object.fromEntries(
  Object.entries(dbKpiId).map(([key, value]) => [value, key])
) as Record<string, string>;

/**
 * Per-field value translators for the cases the generic key convention cannot
 * express, because the label lives in an unrelated i18n namespace. Keyed on the wire
 * (snake_case) field name; the lookup normalises, since `normalize` binds every param
 * under both spellings and the camelCase one is what a message usually interpolates.
 * Shrinks to nothing once DataLayer ships `x-i18n-values`.
 */
const VALUE_TRANSLATORS: Record<string, (raw: string) => string> = {
  'compute_kpis_error.kpi_id': (raw) => `kpis.${backendKpiIdMap[raw] ?? raw}`
};

/**
 * Fields whose value names an entity *type* rather than an entity, so the dataspace's own
 * name for it is the right rendering. The singular is the number the only such message
 * needs — it reads "its Site compatibility requirement", where the type qualifies one
 * requirement. Keyed `code.field` rather than inferred from the value: a rule of "translate
 * anything shaped like an entity type" would rewrite an unrelated field whose value happens
 * to read `SITE`.
 * TODO(DL entity-type annotation): drop this once the property carries a format of its own.
 */
const ENTITY_TYPE_FIELDS = new Set(['compatibility_cfg_compile_error.target_entity_type']);

@Injectable({ providedIn: 'root' })
export class WarningPresenter {
  private readonly translate = inject(TranslateService);
  private readonly parser = inject(TranslateParser);
  private readonly catalog = inject(ErrorCatalogService);
  private readonly entityDisplayNames = inject(EntityDisplayNamesService);

  present(message: CodedStatusMessage): WarningView[] {
    const normalized = normalize(message);
    const byCode = new Map<string, NormalizedWarning[]>();
    for (const entry of normalized) {
      byCode.set(entry.code, [...(byCode.get(entry.code) ?? []), entry]);
    }

    return [...byCode.entries()].map(([code, entries]) => ({
      code,
      title: this.titleOf(code),
      occurrences: entries.map((entry) => this.presentEntry(entry)),
      raw: message
    }));
  }

  presentEntry(entry: NormalizedWarning): WarningOccurrence {
    const rendered = this.resolveBody(entry);
    const size = findingSize(entry);

    return {
      body: rendered.body,
      refs: entry.refs,
      fields: this.buildFields(entry, rendered.placeholders),
      parts: rendered.parts,
      ...(size ? { size } : {}),
      // Spread rather than assigned so an entry naming no record keeps the key absent
      // altogether, which is what distinguishes a global finding from a row-scoped one.
      ...(entry.rowIndices ? { rowIndices: entry.rowIndices } : {})
    };
  }

  /**
   * Every non-reference payload value, formatted and labelled as the fields table shows it,
   * including those the prose consumed: two entries can then be compared value by value even
   * where the sentence is all a reader sees of them.
   */
  formattedPayload(entry: NormalizedWarning): FormattedField[] {
    return this.buildFields(entry, new Set());
  }

  /**
   * The message ladder: DataLayer's `x-i18n-message` for the active language, then GFE's own
   * `gbe_errors.<code>.message`, then DataLayer's prose for this entry.
   *
   * DataLayer's template wins so that a code it describes needs no GFE translation at all;
   * a `gbe_errors` entry is the deliberate override for the cases where GFE says it better.
   *
   * The placeholders and parts travel with the body because they must come from the rung
   * that actually rendered, not from the one GFE happens to hold: the placeholders decide
   * which payload keys the fields table skips as already-said, and the parts split that
   * very sentence.
   */
  private resolveBody(entry: NormalizedWarning): {
    body: string;
    placeholders: Set<string>;
    parts: MessagePart[];
  } {
    const template = this.localized(this.catalog.forCode(entry.code)?.message);
    if (template !== undefined) {
      const placeholders = placeholdersIn(template);
      const params = this.formatParams(entry, placeholders);
      const body = interpolateStrictTemplate(this.parser, template, params);
      if (body !== null) {
        const parts = this.messageParts(entry, body, params, (marked) =>
          this.parser.interpolate(template, marked)
        );
        return { body, placeholders, parts };
      }
    }

    const key = `gbe_errors.${entry.code}.message`;
    const placeholders = this.templatePlaceholders(key);
    const params = this.formatParams(entry, placeholders);
    const body = interpolateStrict(this.translate, key, params);
    if (body !== null) {
      const parts = this.messageParts(entry, body, params, (marked) =>
        this.translate.instant(key, marked)
      );
      return { body, placeholders, parts };
    }

    // `entry.message`, not the message `present` was handed: a `multiple_errors` inner
    // error carries its own prose, and for a code nothing above could render that prose is
    // the whole of what the reader learns. It names no placeholder, so it consumes no
    // payload key and the fields table keeps every one of them.
    return {
      body: entry.message,
      placeholders: new Set(),
      parts: [{ kind: 'text', text: entry.message }]
    };
  }

  /**
   * Splits `body` into the stretches its template wrote and the values it interpolated, by
   * rendering the same template again with every string value swapped for a marker. A split
   * that does not join back into `body` is dropped for the whole sentence as one text part,
   * since a part list that reads differently from `body` would show the reader a sentence
   * that copy and the finding card do not.
   */
  private messageParts(
    entry: NormalizedWarning,
    body: string,
    params: Record<string, unknown>,
    render: (marked: Record<string, unknown>) => unknown
  ): MessagePart[] {
    const whole: MessagePart[] = [{ kind: 'text', text: body }];
    const slots: Slot[] = [];
    const rendered = render(this.markStrings(params, [], slots));
    if (typeof rendered !== 'string') return whole;

    const parts: MessagePart[] = [];
    let end = 0;
    for (const match of rendered.matchAll(SLOT_MARKER)) {
      if (match.index > end) {
        parts.push({ kind: 'text', text: rendered.slice(end, match.index) });
      }
      parts.push(this.slotPart(entry, slots[Number(match[1])]));
      end = match.index + match[0].length;
    }
    if (end < rendered.length) parts.push({ kind: 'text', text: rendered.slice(end) });

    return parts.map((part) => part.text).join('') === body ? parts : whole;
  }

  /** A copy of `params` with each string leaf replaced by its slot's marker. */
  private markStrings(
    params: Record<string, unknown>,
    path: string[],
    slots: Slot[]
  ): Record<string, unknown> {
    const marked: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'string') {
        marked[key] = slotMarker(slots.length);
        slots.push({ path: [...path, key], text: value });
      } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        marked[key] = this.markStrings(
          value as Record<string, unknown>,
          [...path, key],
          slots
        );
      } else {
        marked[key] = value;
      }
    }
    return marked;
  }

  private slotPart(entry: NormalizedWarning, slot: Slot): MessagePart {
    const { path, text } = slot;
    if (path.length === 1 && path[0].startsWith('@')) {
      return { kind: 'refs', text, refs: relationRefs(entry.refs, path[0]) };
    }
    const raw = this.rawAt(entry, path);
    if (typeof raw === 'string') {
      const kind = printedKind(raw, text, this.translate.getCurrentLang());
      if (kind !== null) return { kind, text, iso: raw };
    }
    return { kind: 'value', text };
  }

  /**
   * The unformatted value behind a param path. A nested path reads the payload, which keeps
   * the struct `params` flattens; its root may be the camelCase alias the template used.
   */
  private rawAt(entry: NormalizedWarning, path: string[]): unknown {
    const [root, ...leaves] = path;
    if (leaves.length === 0) return entry.params[root];
    let value: unknown = entry.payload[root] ?? entry.payload[toSnakeCase(root)];
    for (const leaf of leaves) {
      value =
        value !== null && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)[leaf]
          : undefined;
    }
    return value;
  }

  titleOf(code: string): string {
    const annotated = this.localized(this.catalog.forCode(code)?.title);
    if (annotated !== undefined) return annotated;
    const key = `gbe_errors.${code}.title`;
    const translated = this.translate.instant(key);
    return translated === key ? code : translated;
  }

  /** The active language's text, falling back to English rather than rendering nothing. */
  private localized(text: LocalizedText | undefined): string | undefined {
    if (!text) return undefined;
    return text[this.translate.getCurrentLang()] ?? text['en'];
  }

  /** Formats every param value, so a template renders "1h 30min" rather than "PT1H30M". */
  private formatParams(
    entry: NormalizedWarning,
    placeholders: Set<string>
  ): Record<string, unknown> {
    const formatted: Record<string, unknown> = { ...entry.params };
    for (const [key, value] of Object.entries(entry.params)) {
      if (typeof value === 'string') {
        formatted[key] = this.formatValue(entry.code, key, value);
      } else if (typeof value === 'number') {
        formatted[key] = formatQuantity(value);
      }
    }
    // A nested payload object is carried through unflattened only for the template that
    // reaches into it, since doing so replaces the readable `k: v, k: v` string
    // `normalize` bound — which is what `{{parameters}}` renders for
    // `data_not_found_error`, where the whole struct is named by one placeholder.
    for (const [key, value] of Object.entries(entry.payload)) {
      const annotated = this.catalog.forCode(entry.code)?.formats?.[key];
      if (annotated?.format === 'ENTITY_REFERENCE') {
        const record = value as Record<string, unknown>;
        const entityType = record['entity_type'];
        if (typeof entityType === 'string') {
          const uid = typeof record['uid'] === 'string' ? record['uid'] : null;
          const label = this.labelForReference(entityType, uid);
          formatted[key] = label;
          formatted[toCamelCase(key)] = label;
          continue;
        }
      }
      // An annotated array of uids names one entity per element, but `normalize` binds it as
      // a single comma-joined string — which `formatValue` would render as one label whose
      // uid reads "T1, T2". Joined the way `relationParams` joins a plural relation, so a
      // list of references reads identically wherever it appears.
      const reference = annotated?.reference;
      if (
        annotated?.format === 'UID' &&
        reference !== undefined &&
        Array.isArray(value) &&
        value.every((item): item is string => typeof item === 'string')
      ) {
        const labels = new Intl.ListFormat(this.translate.getCurrentLang(), {
          style: 'long',
          type: 'conjunction'
        }).format(value.map((uid) => this.labelForReference(reference, uid)));
        formatted[key] = labels;
        formatted[toCamelCase(key)] = labels;
        continue;
      }
      // An array of a plain enum names one variant per element, but `normalize` binds it as
      // a single comma-joined string of raw identifiers — the same gap the UID-array branch
      // above closes for references. Joined the same way, falling back to the raw value per
      // element so one unrecognized variant doesn't blank the whole list.
      const enumValues = this.catalog.forCode(entry.code)?.enumValues?.[key];
      if (
        enumValues &&
        Array.isArray(value) &&
        value.every((item): item is string => typeof item === 'string')
      ) {
        const labels = new Intl.ListFormat(this.translate.getCurrentLang(), {
          style: 'long',
          type: 'conjunction'
        }).format(
          value.map((variant) => this.enumLabel(entry.code, key, variant) ?? variant)
        );
        formatted[key] = labels;
        formatted[toCamelCase(key)] = labels;
        continue;
      }
      if (value === null || typeof value !== 'object' || Array.isArray(value)) continue;
      // A template naming a leaf outright wins over the variant rendering, which only
      // has a label for the struct as a whole.
      if (!this.hasDottedPlaceholder(key, placeholders)) {
        const tagged = this.formatTaggedVariant(entry.code, key, value);
        if (tagged !== undefined) {
          formatted[key] = tagged;
          formatted[toCamelCase(key)] = tagged;
        }
        continue;
      }
      const nested = this.formatNested(entry.code, value as Record<string, unknown>);
      // Bound under both spellings, like every scalar param: a template addressing
      // `{{routeWindow.start}}` would otherwise still see the joined `k: v` string,
      // leave the placeholder unbound and drop the whole message to DataLayer's prose.
      formatted[key] = nested;
      formatted[toCamelCase(key)] = nested;
    }
    // Merged last so these never enter the loop above: an `@` value is an already-rendered
    // label, and `formatValue` would reinterpret a uid such as `P1D` as a duration.
    return {
      ...formatted,
      ...relationParams(
        entry.refs,
        placeholders,
        (ref) =>
          refLabel(ref, this.translate, this.entityDisplayNames.singularDisplayNames()),
        this.translate.getCurrentLang()
      )
    };
  }

  /**
   * Renders serde's externally-tagged enum — `{ "Code": "1174" }` for a Rust variant —
   * as "<variant label> <value>", so a message reads "registration code 1174" instead of
   * leaking the variant identifier through the generic object flatten as "Code: 1174".
   *
   * Gated on the bundle actually naming the variant rather than on the key's shape: a
   * struct that merely happens to hold one field keeps the `k: v` rendering, so opting a
   * field in is a translation-only change. Goes away once DataLayer either flattens these
   * or ships `x-i18n-values`.
   */
  private formatTaggedVariant(
    code: string,
    field: string,
    value: unknown
  ): string | undefined {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return undefined;
    }
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length !== 1) return undefined;
    const [variant, inner] = entries[0];
    if (inner === null || typeof inner === 'object') return undefined;
    const label = this.enumLabel(code, toSnakeCase(field), variant);
    return label === undefined ? undefined : `${label} ${String(inner)}`;
  }

  /**
   * The label the bundle gives one value of an enum field, or undefined when it names
   * none. The capitalized spelling is tried as well because DataLayer's variant casing
   * is not part of the contract — a serde `rename_all` turning `Late` into `late` would
   * otherwise render the value raw and untranslated instead of failing visibly.
   */
  private enumLabel(code: string, field: string, value: string): string | undefined {
    const annotated = this.catalog.forCode(code)?.enumValues?.[field];
    for (const variant of new Set([value, capitalizeFirst(value)])) {
      const label = this.localized(annotated?.[variant]);
      if (label !== undefined) return label;
    }
    for (const variant of new Set([value, capitalizeFirst(value)])) {
      const key = `gbe_errors.${code}.${field}.${variant}`;
      const translated = this.translate.instant(key);
      if (translated !== key) return translated;
    }
    return undefined;
  }

  /** Whether the template addresses a leaf of `key` (`{{key.leaf}}`), under either spelling. */
  private hasDottedPlaceholder(key: string, placeholders: Set<string>): boolean {
    const roots = [`${key}.`, `${toCamelCase(key)}.`];
    return [...placeholders].some((name) => roots.some((root) => name.startsWith(root)));
  }

  /**
   * Formats the scalar leaves of a nested payload object, so a dotted placeholder
   * renders like a top-level one — `{{route_window.start}}` reads "Jul 29, 2026, 01:00 AM",
   * not "2026-07-29T01:00:00". The leaf's own name is the field name for the enum and
   * duration lookups, matching how a flat payload key resolves.
   */
  private formatNested(
    code: string,
    value: Record<string, unknown>
  ): Record<string, unknown> {
    const formatted: Record<string, unknown> = {};
    for (const [key, leaf] of Object.entries(value)) {
      if (typeof leaf === 'string') {
        formatted[key] = this.formatValue(code, key, leaf);
      } else if (typeof leaf === 'number') {
        formatted[key] = formatQuantity(leaf);
      } else if (leaf !== null && typeof leaf === 'object' && !Array.isArray(leaf)) {
        formatted[key] = this.formatNested(code, leaf as Record<string, unknown>);
      } else {
        formatted[key] = leaf;
      }
    }
    return formatted;
  }

  /**
   * A reference label for a payload value, so a uid in prose reads like one in a chip.
   * The type is normalised because the display names are keyed on the upper-cased wire
   * spelling, while a type reaching here — from a payload value or from a schema's
   * `x-reference` — can be PascalCase, as DataLayer's Rust enums are.
   */
  private labelForReference(entityType: string, uid: string | null): string {
    return refLabel(
      { entityType: toScreamingSnakeCase(entityType), uid },
      this.translate,
      this.entityDisplayNames.singularDisplayNames()
    );
  }

  private formatValue(code: string, field: string, raw: string): string {
    const wireField = toSnakeCase(field);
    const annotated = this.catalog.forCode(code)?.formats?.[wireField];
    // A uid whose referent is unknown is opaque: returned raw rather than falling through,
    // since a field name outside `UID_FIELD` would otherwise reach the duration formatter
    // and rewrite an identifier such as `P1D` into "1d".
    if (annotated?.format === 'UID') {
      return annotated.reference === undefined
        ? raw
        : this.labelForReference(annotated.reference, raw);
    }

    // The display names are keyed on the upper-cased wire type, and a value carrying an
    // entity type can reach the frontend PascalCase from DataLayer's Rust enums.
    if (ENTITY_TYPE_FIELDS.has(`${code}.${wireField}`)) {
      return (
        this.entityDisplayNames.singularDisplayNames().get(toScreamingSnakeCase(raw)) ?? raw
      );
    }

    const translator = VALUE_TRANSLATORS[`${code}.${wireField}`];
    if (translator) {
      const key = translator(raw);
      const translated = this.translate.instant(key);
      return translated === key ? raw : translated;
    }

    const enumLabel = this.enumLabel(code, wireField, raw);
    if (enumLabel !== undefined) {
      return enumLabel;
    }

    // A uid is opaque, and this domain mints identifiers that parse as ISO durations
    // ("P1D"), so formatting one would replace the identifier with a wrong duration
    // rather than merely restyling it.
    if (UID_FIELD.test(wireField)) {
      return raw;
    }

    if (isIsoDuration(raw)) {
      return formatDurationFromIso(raw);
    }
    return formatIsoDateTimeAsStated(raw, this.translate.getCurrentLang()) ?? raw;
  }

  /**
   * Names ngx-translate left as `{{name}}` in the raw (uninterpolated) template, read by
   * calling `instant` with no params — `DefaultInterpolator.interpolateString` returns the
   * expr unchanged when params is falsy, so this is the exact placeholder set the message
   * rung tried to fill, before any values were substituted in.
   */
  private templatePlaceholders(messageKey: string): Set<string> {
    const template = this.translate.instant(messageKey);
    return typeof template === 'string' ? placeholdersIn(template) : new Set();
  }

  /** Every payload value the prose did not consume as a placeholder, so nothing is silently dropped. */
  private buildFields(
    entry: NormalizedWarning,
    placeholders: Set<string>
  ): FormattedField[] {
    const fields: FormattedField[] = [];
    for (const [key, value] of Object.entries(entry.payload)) {
      // A uid names an entity the reference chips already show, so listing it here too
      // renders it twice — matching the reason `liftNestedPayload` drops uid leaves.
      if (UID_FIELD.test(key)) continue;
      // `normalize` binds a payload key under both spellings (`oper_kind` and `operKind`),
      // so a template may reference either; check both before deciding the field was used.
      if (placeholders.has(key) || placeholders.has(toCamelCase(key))) continue;
      const unconsumed = this.dropConsumedLeaves(key, value, placeholders);
      const rendered =
        typeof unconsumed === 'string'
          ? this.formatValue(entry.code, key, unconsumed)
          : (this.formatTaggedVariant(entry.code, key, unconsumed) ??
            this.renderNonScalar(unconsumed));
      if (rendered === '') continue;
      fields.push({
        key,
        label: this.fieldLabel(entry.code, key),
        value: rendered,
        ...(typeof value === 'string' ? { raw: value } : {})
      });
    }
    return fields;
  }

  /**
   * Drops the leaves a dotted placeholder already rendered, leaf by leaf rather than
   * the whole struct: `{{route_window.start}}` must not take `route_window.finish` down
   * with it, since a value no placeholder read has nowhere else to appear. A struct
   * whose every leaf is consumed renders empty, which the caller drops.
   *
   * Only one level deep, matching the one level `formatNested` binds; a deeper struct
   * stays whole, erring towards showing a value twice rather than losing it.
   */
  private dropConsumedLeaves(
    key: string,
    value: unknown,
    placeholders: Set<string>
  ): unknown {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    const roots = [key, toCamelCase(key)];
    const unconsumed: Record<string, unknown> = {};
    for (const [leaf, inner] of Object.entries(value as Record<string, unknown>)) {
      const consumed = roots.some((root) =>
        [leaf, toCamelCase(leaf)].some((name) => placeholders.has(`${root}.${name}`))
      );
      if (!consumed) {
        unconsumed[leaf] = inner;
      }
    }
    return unconsumed;
  }

  private renderNonScalar(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return formatQuantity(value);
    if (Array.isArray(value)) {
      return value
        .map((item) =>
          item !== null && typeof item === 'object'
            ? this.renderNonScalar(item)
            : this.renderScalarLeaf(item)
        )
        .join('; ');
    }
    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>)
        .map(([key, inner]) => `${key}: ${this.renderScalarLeaf(inner)}`)
        .join(', ');
    }
    return String(value);
  }

  /** A struct/array leaf as display text: floats rounded, everything else stringified. */
  private renderScalarLeaf(value: unknown): string {
    return typeof value === 'number' ? formatQuantity(value) : String(value);
  }

  private fieldLabel(code: string, key: string): string {
    const scoped = `gbe_errors.${code}.fields.${key}`;
    const translated = this.translate.instant(scoped);
    if (translated !== scoped) return translated;
    return capitalizeFirst(key.replace(/_/g, ' '));
  }
}
